<template>
	<div>
		<Breadcrumbs main="System Management" title="System Settings" />
		<div class="container-fluid">
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>Background Daemon Service</h5>
								<span>Monitor runtime health and control AutoDraw execution.</span>
							</div>
							<button class="btn btn-outline-primary" type="button" @click="fetchStatus" :disabled="loading || toggling">
								Refresh
							</button>
						</div>

						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>
							<div v-if="successMessage" class="alert alert-success" role="alert">{{ successMessage }}</div>

							<div class="status-grid">
								<div class="status-item">
									<div class="label">Daemon Status</div>
									<span :class="statusBadgeClass">{{ displayStatus }}</span>
								</div>
								<div class="status-item">
									<div class="label">Service Switch</div>
									<button
										class="btn"
										:class="status.isEnabled ? 'btn-danger' : 'btn-success'"
										type="button"
										:disabled="toggling || !canToggle"
										@click="toggleDaemon"
									>
										{{ toggling ? 'Processing...' : (status.isEnabled ? 'Turn OFF' : 'Turn ON') }}
									</button>
								</div>
							</div>

							<hr />

							<div class="row g-3 text-muted small">
								<div class="col-md-6">
									<strong>Last Heartbeat:</strong> {{ formatDate(status.lastHeartbeatUtc) }}
								</div>
								<div class="col-md-6">
									<strong>Last Create Draw Run:</strong> {{ formatDate(status.lastCreateDrawRunUtc) }}
								</div>
								<div class="col-md-6">
									<strong>Last Close Bet Run:</strong> {{ formatDate(status.lastCloseDrawBetRunUtc) }}
								</div>
								<div class="col-md-6">
									<strong>Last Conduct Draw Run:</strong> {{ formatDate(status.lastConductAutoDrawRunUtc) }}
								</div>
							</div>

							<div v-if="!canToggle" class="alert alert-warning mt-3 mb-0" role="alert">
								You do not have permission to toggle the daemon service.
							</div>

							<hr class="my-4" />

							<div>
								<h6 class="mb-2">Main Header Logo</h6>
								<p class="text-muted small mb-3">Upload a logo image for the header logo-wrapper section.</p>

								<div class="logo-preview-wrap mb-3">
									<img :src="mainHeaderLogoPreviewUrl" class="logo-preview" alt="Main header logo preview" />
								</div>

								<div class="row g-2 align-items-end">
									<div class="col-md-8">
										<label class="form-label">Logo Image</label>
										<input
											ref="mainHeaderLogoInput"
											type="file"
											class="form-control"
											accept="image/*"
											:disabled="!canToggle || uploadingMainHeaderLogo"
											@change="handleMainHeaderLogoChange"
										/>
									</div>
									<div class="col-md-4">
										<button
											class="btn btn-primary w-100"
											type="button"
											:disabled="!canToggle || !mainHeaderLogoFile || uploadingMainHeaderLogo"
											@click="uploadMainHeaderLogo"
										>
											{{ uploadingMainHeaderLogo ? 'Uploading...' : 'Update Header Logo' }}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { API_BASE } from '../../config/api';
const DEFAULT_LOGO = require('../../assets/images/endless-logo.png');

export default {
	name: 'SystemIndex',
	data() {
		return {
			baseUrl: `${API_BASE}/api/System`,
			loading: false,
			toggling: false,
			errorMessage: '',
			successMessage: '',
			uploadingMainHeaderLogo: false,
			mainHeaderLogoUrl: '',
			mainHeaderLogoFile: null,
			mainHeaderLogoPreviewObjectUrl: '',
			status: {
				isEnabled: false,
				isRunning: false,
				status: 'STOPPED',
				lastHeartbeatUtc: null,
				lastCreateDrawRunUtc: null,
				lastCloseDrawBetRunUtc: null,
				lastConductAutoDrawRunUtc: null,
			},
		};
	},
	computed: {
		permissions() {
			return this.$store?.state?.auth?.permissions || [];
		},
		canRead() {
			return this.permissions.includes('systems:read');
		},
		canToggle() {
			return this.permissions.includes('systems:update');
		},
		displayStatus() {
			return this.status.isRunning ? 'RUNNIING' : 'STOPPED';
		},
		statusBadgeClass() {
			return this.status.isRunning ? 'badge badge-success px-3 py-2' : 'badge badge-danger px-3 py-2';
		},
		mainHeaderLogoPreviewUrl() {
			if (this.mainHeaderLogoPreviewObjectUrl) {
				return this.mainHeaderLogoPreviewObjectUrl;
			}

			if (!this.mainHeaderLogoUrl) {
				return DEFAULT_LOGO;
			}

			if (this.mainHeaderLogoUrl.startsWith('http')) {
				return this.mainHeaderLogoUrl;
			}

			return `${API_BASE}${this.mainHeaderLogoUrl}`;
		},
	},
	unmounted() {
		if (this.mainHeaderLogoPreviewObjectUrl) {
			URL.revokeObjectURL(this.mainHeaderLogoPreviewObjectUrl);
		}
	},
	mounted() {
		if (!this.canRead) {
			this.errorMessage = 'You do not have permission to view system settings.';
			return;
		}

		this.fetchStatus();
		this.fetchMainHeaderLogo();
	},
	methods: {
		getHeaders() {
			return {
				Authorization: `Bearer ${localStorage.getItem('token')}`,
				'Content-Type': 'application/json',
			};
		},
		async fetchStatus() {
			this.loading = true;
			this.errorMessage = '';
			this.successMessage = '';

			try {
				const response = await fetch(`${this.baseUrl}/daemon-status`, {
					method: 'GET',
					headers: this.getHeaders(),
				});

				if (!response.ok) {
					throw new Error(`Failed to fetch daemon status (${response.status})`);
				}

				const data = await response.json();
				this.status = {
					...this.status,
					...data,
				};
			} catch (error) {
				this.errorMessage = error?.message || 'Unable to load daemon status.';
			} finally {
				this.loading = false;
			}
		},
		async fetchMainHeaderLogo() {
			try {
				const response = await fetch(`${this.baseUrl}/main-header-logo`, {
					method: 'GET',
					headers: this.getHeaders(),
				});

				if (!response.ok) {
					throw new Error(`Failed to fetch main header logo (${response.status})`);
				}

				const data = await response.json();
				this.mainHeaderLogoUrl = data.logoUrl || '';
			} catch (error) {
				this.errorMessage = error?.message || 'Unable to load header logo settings.';
			}
		},
		handleMainHeaderLogoChange(event) {
			const file = event.target.files?.[0] || null;
			this.mainHeaderLogoFile = file;

			if (this.mainHeaderLogoPreviewObjectUrl) {
				URL.revokeObjectURL(this.mainHeaderLogoPreviewObjectUrl);
				this.mainHeaderLogoPreviewObjectUrl = '';
			}

			if (file) {
				this.mainHeaderLogoPreviewObjectUrl = URL.createObjectURL(file);
			}
		},
		async uploadMainHeaderLogo() {
			if (!this.canToggle) {
				this.errorMessage = 'You do not have permission to update system settings.';
				return;
			}

			if (!this.mainHeaderLogoFile) {
				this.errorMessage = 'Please select a logo image first.';
				return;
			}

			this.uploadingMainHeaderLogo = true;
			this.errorMessage = '';
			this.successMessage = '';

			try {
				const formData = new FormData();
				formData.append('logo', this.mainHeaderLogoFile);

				const response = await fetch(`${this.baseUrl}/main-header-logo`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${localStorage.getItem('token')}`,
					},
					body: formData,
				});

				if (!response.ok) {
					throw new Error(`Failed to update main header logo (${response.status})`);
				}

				const data = await response.json();
				this.mainHeaderLogoUrl = data.logoUrl || '';
				this.successMessage = data.message || 'Main header logo updated.';

				if (this.$refs.mainHeaderLogoInput) {
					this.$refs.mainHeaderLogoInput.value = '';
				}
				this.mainHeaderLogoFile = null;
				if (this.mainHeaderLogoPreviewObjectUrl) {
					URL.revokeObjectURL(this.mainHeaderLogoPreviewObjectUrl);
					this.mainHeaderLogoPreviewObjectUrl = '';
				}

				window.dispatchEvent(new CustomEvent('system-logo-updated'));
			} catch (error) {
				this.errorMessage = error?.message || 'Unable to update main header logo.';
			} finally {
				this.uploadingMainHeaderLogo = false;
			}
		},
		async toggleDaemon() {
			if (!this.canToggle) {
				this.errorMessage = 'You do not have permission to toggle daemon status.';
				return;
			}

			this.toggling = true;
			this.errorMessage = '';
			this.successMessage = '';

			try {
				const targetEnabledState = !this.status.isEnabled;
				const response = await fetch(`${this.baseUrl}/daemon-toggle`, {
					method: 'POST',
					headers: this.getHeaders(),
					body: JSON.stringify({ isEnabled: targetEnabledState }),
				});

				if (!response.ok) {
					throw new Error(`Failed to update daemon status (${response.status})`);
				}

				const result = await response.json();
				this.successMessage = result.message || 'Daemon status updated.';
				await this.fetchStatus();
			} catch (error) {
				this.errorMessage = error?.message || 'Unable to update daemon status.';
			} finally {
				this.toggling = false;
			}
		},
		formatDate(value) {
			if (!value) return '-';
			const date = new Date(value);
			if (Number.isNaN(date.getTime())) return '-';
			return date.toLocaleString('en-GB');
		},
	},
};
</script>

<style scoped>
.status-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 1.5rem;
}

.status-item {
	min-width: 220px;
}

.label {
	font-size: 0.85rem;
	color: #6c757d;
	margin-bottom: 0.4rem;
}

.logo-preview-wrap {
	width: 100%;
	max-width: 320px;
	padding: 0.5rem;
	border: 1px dashed #ced4da;
	border-radius: 0.5rem;
	background: #f8f9fa;
}

.logo-preview {
	width: 100%;
	height: 70px;
	object-fit: contain;
	background: #ffffff;
}
</style>
